import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Users, Mail, Phone, Building2, Calendar, Loader2, Clock,
  CheckCircle, XCircle, Eye, MessageSquare, Filter, Search, UserCheck
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { parseDateLocal } from "@/lib/utils";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface DirectReport {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  department: string;
  position: string;
  profilePicture: string | null;
  hireDate: string | null;
  status: string;
}

interface TeamData {
  directReports: DirectReport[];
  isManager: boolean;
  employeeId: number;
}

interface TeamTimeOffRequest {
  request: {
    id: number;
    employeeId: number;
    policyId: number;
    startDate: string;
    endDate: string;
    totalHours: string;
    requestType: string;
    status: string;
    reason?: string;
    denialReason?: string;
    notes?: string;
    requestedAt?: string;
    createdAt?: string;
    reviewedAt?: string;
    reviewedBy?: string;
  };
  policy: {
    id: number;
    name: string;
    color?: string;
  } | null;
  employee: {
    id: number;
    firstName: string;
    lastName: string;
    email?: string;
    department?: string;
    position?: string;
    profilePicture?: string;
  } | null;
  canApprove?: boolean;
}

export default function Team() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [filterStatus, setFilterStatus] = useState<string>("pending");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRequest, setSelectedRequest] = useState<TeamTimeOffRequest | null>(null);
  const [actionType, setActionType] = useState<"approve" | "deny" | null>(null);
  const [approvalNotes, setApprovalNotes] = useState("");
  const [denialReason, setDenialReason] = useState("");

  const { data: teamData, isLoading: teamLoading, error: teamError } = useQuery<TeamData>({
    queryKey: ["/api/employees/team"],
  });

  const { data: teamRequests = [], isLoading: requestsLoading } = useQuery<TeamTimeOffRequest[]>({
    queryKey: ["/api/time-off/requests", "team"],
    queryFn: async () => {
      const res = await fetch("/api/time-off/requests?scope=team", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch team requests");
      return res.json();
    },
  });

  const directReports = teamData?.directReports || [];

  const approveMutation = useMutation({
    mutationFn: async ({ id, notes }: { id: number; notes?: string }) => {
      return apiRequest("PATCH", `/api/time-off/requests/${id}/approve`, { notes });
    },
    onSuccess: () => {
      toast({
        title: "Request Approved",
        description: "The time off request has been approved.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/time-off/requests"] });
      setSelectedRequest(null);
      setActionType(null);
      setApprovalNotes("");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const denyMutation = useMutation({
    mutationFn: async ({ id, denialReason }: { id: number; denialReason: string }) => {
      return apiRequest("PATCH", `/api/time-off/requests/${id}/deny`, { denialReason });
    },
    onSuccess: () => {
      toast({
        title: "Request Denied",
        description: "The time off request has been denied.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/time-off/requests"] });
      setSelectedRequest(null);
      setActionType(null);
      setDenialReason("");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const isMutating = approveMutation.isPending || denyMutation.isPending;

  const handleReview = (req: TeamTimeOffRequest) => {
    setSelectedRequest(req);
    setActionType(null);
    setApprovalNotes("");
    setDenialReason("");
  };

  const confirmAction = () => {
    if (!selectedRequest) return;
    if (actionType === "approve") {
      approveMutation.mutate({ id: selectedRequest.request.id, notes: approvalNotes || undefined });
    } else if (actionType === "deny") {
      if (!denialReason.trim()) {
        toast({ title: "Reason required", description: "Please provide a reason for denial.", variant: "destructive" });
        return;
      }
      denyMutation.mutate({ id: selectedRequest.request.id, denialReason });
    }
  };

  // Filter requests
  const filteredRequests = teamRequests.filter(r => {
    const matchesStatus = filterStatus === 'all' || r.request.status === filterStatus;
    const employeeName = `${r.employee?.firstName || ''} ${r.employee?.lastName || ''}`.toLowerCase();
    const matchesSearch = !searchTerm ||
      employeeName.includes(searchTerm.toLowerCase()) ||
      (r.employee?.department?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (r.policy?.name.toLowerCase().includes(searchTerm.toLowerCase()));

    return matchesStatus && matchesSearch;
  });

  const pendingCount = teamRequests.filter(r => r.request.status === "pending").length;

  if (teamLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (teamError) {
    return (
      <div className="text-center py-12 text-red-500">
        Failed to load team information
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">My Team</h1>
          <p className="text-muted-foreground mt-1">
            {directReports.length} direct report{directReports.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      <Tabs defaultValue="team" className="space-y-4">
        <TabsList>
          <TabsTrigger value="team">Team Members</TabsTrigger>
          <TabsTrigger value="time-off" className="gap-2">
            Time Off Requests
            {pendingCount > 0 && (
              <Badge variant="secondary" className="ml-1">
                {pendingCount}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Team Members Tab */}
        <TabsContent value="team" className="space-y-4">
          {directReports.length === 0 ? (
            <Card>
              <CardContent className="py-12">
                <div className="text-center text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">No Direct Reports</p>
                  <p className="text-sm mt-2">
                    You don't have any employees reporting to you.
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {directReports.map((employee) => (
                <Card key={employee.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex items-start gap-4">
                      <Avatar className="h-12 w-12">
                        {employee.profilePicture && (
                          <AvatarImage src={employee.profilePicture} />
                        )}
                        <AvatarFallback>
                          {employee.firstName?.[0]}
                          {employee.lastName?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-base truncate">
                          {employee.firstName} {employee.lastName}
                        </CardTitle>
                        <p className="text-sm text-muted-foreground truncate">
                          {employee.position}
                        </p>
                        {employee.status && employee.status !== 'active' && (
                          <Badge variant="secondary" className="mt-1 text-xs">
                            {employee.status}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3 pt-0">
                    <div className="flex items-center gap-2 text-sm">
                      <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <span className="truncate">{employee.department}</span>
                    </div>

                    {employee.email && (
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <a
                          href={`mailto:${employee.email}`}
                          className="text-primary hover:underline truncate"
                        >
                          {employee.email}
                        </a>
                      </div>
                    )}

                    {employee.phone && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <a
                          href={`tel:${employee.phone}`}
                          className="text-primary hover:underline"
                        >
                          {employee.phone}
                        </a>
                      </div>
                    )}

                    {employee.hireDate && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4 flex-shrink-0" />
                        <span>Since {format(parseDateLocal(employee.hireDate) ?? new Date(employee.hireDate), 'MMM yyyy')}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Time Off Requests Tab */}
        <TabsContent value="time-off" className="space-y-4">
          {/* Metrics Overview */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Pending Approvals</CardTitle>
                <Clock className="h-4 w-4 text-orange-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">
                  {requestsLoading ? '—' : pendingCount}
                </div>
                <p className="text-xs text-muted-foreground">
                  Require your approval
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Team Members</CardTitle>
                <Users className="h-4 w-4 text-blue-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">
                  {directReports.length}
                </div>
                <p className="text-xs text-muted-foreground">
                  Direct reports
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Requests</CardTitle>
                <CheckCircle className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {requestsLoading ? '—' : teamRequests.length}
                </div>
                <p className="text-xs text-muted-foreground">
                  All time off requests
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Filter className="h-4 w-4" />
                Filters
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Search</Label>
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name..."
                      className="pl-8"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="denied">Denied</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>&nbsp;</Label>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setFilterStatus('pending');
                      setSearchTerm('');
                    }}
                  >
                    Clear Filters
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Approval Queue */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserCheck className="h-5 w-5" />
                Time Off Requests ({requestsLoading ? '...' : filteredRequests.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {requestsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : filteredRequests.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No time off requests match your filters.</p>
                  </div>
                ) : (
                  filteredRequests.map((r) => (
                    <div key={r.request.id} className="p-4 border rounded-lg hover:bg-muted/25 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-8 w-8">
                              {r.employee?.profilePicture && (
                                <AvatarImage src={r.employee.profilePicture} />
                              )}
                              <AvatarFallback className="text-xs">
                                {r.employee?.firstName?.[0]}
                                {r.employee?.lastName?.[0]}
                              </AvatarFallback>
                            </Avatar>
                            <h3 className="font-semibold">
                              {r.employee?.firstName} {r.employee?.lastName}
                            </h3>
                            {r.employee?.department && (
                              <Badge variant="outline">{r.employee.department}</Badge>
                            )}
                            <Badge variant="secondary">{r.policy?.name}</Badge>
                            <Badge
                              variant={
                                r.request.status === "approved" ? "default" :
                                r.request.status === "pending" ? "secondary" : "destructive"
                              }
                            >
                              {r.request.status.charAt(0).toUpperCase() + r.request.status.slice(1)}
                            </Badge>
                          </div>

                          <div className="grid gap-2 md:grid-cols-2 text-sm text-muted-foreground">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4" />
                              <span>
                                {format(parseISO(r.request.startDate), 'MMM d')} - {format(parseISO(r.request.endDate), 'MMM d, yyyy')}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4" />
                              <span>{parseFloat(r.request.totalHours)}h</span>
                            </div>
                            {r.request.reason && (
                              <div className="flex items-center gap-2 md:col-span-2">
                                <MessageSquare className="h-4 w-4" />
                                <span>Reason: {r.request.reason}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {r.request.status === "pending" && r.canApprove ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleReview(r)}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              Review
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleReview(r)}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              Details
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          <Dialog open={!!selectedRequest} onOpenChange={() => { setSelectedRequest(null); setActionType(null); }}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>
                  {actionType === "approve"
                    ? "Confirm Approval"
                    : actionType === "deny"
                    ? "Confirm Denial"
                    : selectedRequest?.request.status === "pending"
                    ? "Review Request"
                    : "Request Details"}
                </DialogTitle>
              </DialogHeader>
              {selectedRequest && (
                <div className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <Label className="text-sm font-medium">Employee</Label>
                      <p className="text-sm">
                        {selectedRequest.employee?.firstName} {selectedRequest.employee?.lastName}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Department</Label>
                      <p className="text-sm">{selectedRequest.employee?.department || '—'}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Position</Label>
                      <p className="text-sm">{selectedRequest.employee?.position || '—'}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Request Type</Label>
                      <p className="text-sm">{selectedRequest.policy?.name || '—'}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Start Date</Label>
                      <p className="text-sm">{format(parseISO(selectedRequest.request.startDate), 'MMMM d, yyyy')}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">End Date</Label>
                      <p className="text-sm">{format(parseISO(selectedRequest.request.endDate), 'MMMM d, yyyy')}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Hours</Label>
                      <p className="text-sm">{parseFloat(selectedRequest.request.totalHours)}h</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Requested On</Label>
                      <p className="text-sm">
                        {selectedRequest.request.requestedAt
                          ? format(parseISO(selectedRequest.request.requestedAt), 'MMMM d, yyyy')
                          : '—'}
                      </p>
                    </div>
                  </div>

                  {selectedRequest.request.reason && (
                    <div>
                      <Label className="text-sm font-medium">Reason</Label>
                      <p className="text-sm mt-1">{selectedRequest.request.reason}</p>
                    </div>
                  )}

                  {selectedRequest.request.status === 'approved' && (
                    <div className="p-3 bg-green-50 dark:bg-green-950/20 rounded-lg">
                      <p className="text-sm text-green-700 dark:text-green-300">
                        <CheckCircle className="h-4 w-4 inline mr-2" />
                        Approved
                        {selectedRequest.request.reviewedAt &&
                          ` on ${format(parseISO(selectedRequest.request.reviewedAt), 'MMMM d, yyyy')}`}
                      </p>
                    </div>
                  )}

                  {selectedRequest.request.status === 'denied' && (
                    <div className="p-3 bg-red-50 dark:bg-red-950/20 rounded-lg">
                      <p className="text-sm text-red-700 dark:text-red-300">
                        <XCircle className="h-4 w-4 inline mr-2" />
                        Denied{selectedRequest.request.denialReason ? `: ${selectedRequest.request.denialReason}` : ''}
                      </p>
                    </div>
                  )}

                  {actionType === "approve" && (
                    <div className="border-t pt-4">
                      <Label className="text-sm font-medium">Notes (optional)</Label>
                      <Textarea
                        className="mt-2"
                        value={approvalNotes}
                        onChange={(e) => setApprovalNotes(e.target.value)}
                        placeholder="Add any notes for this approval..."
                      />
                    </div>
                  )}

                  {actionType === "deny" && (
                    <div className="border-t pt-4">
                      <Label className="text-sm font-medium">
                        Reason for Denial <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        className="mt-2"
                        value={denialReason}
                        onChange={(e) => setDenialReason(e.target.value)}
                        placeholder="Please provide a reason for denying this request..."
                      />
                    </div>
                  )}
                </div>
              )}
              <DialogFooter>
                {actionType ? (
                  <>
                    <Button variant="outline" onClick={() => setActionType(null)}>
                      Back
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => { setSelectedRequest(null); setActionType(null); }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={confirmAction}
                      disabled={isMutating}
                      className={actionType === "approve" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}
                    >
                      {isMutating
                        ? "Processing..."
                        : actionType === "approve"
                        ? "Confirm Approval"
                        : "Confirm Denial"}
                    </Button>
                  </>
                ) : selectedRequest?.request.status === "pending" && selectedRequest?.canApprove ? (
                  <>
                    <Button
                      variant="outline"
                      className="text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30"
                      onClick={() => setActionType("approve")}
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      variant="outline"
                      className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30"
                      onClick={() => setActionType("deny")}
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Deny
                    </Button>
                  </>
                ) : (
                  <Button variant="outline" onClick={() => setSelectedRequest(null)}>
                    Close
                  </Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
}
